import { CardCaptureStatus, getCardCaptureStatus, hasDocumentCorners, useFaceModel } from '@onefootprint/vision';
import type { MutableRefObject } from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useCountdown } from 'usehooks-ts';
import { getLogger } from '@/idv2/utils/logger';
import { REQUIRED_SUCCESSES, SELFIE_CHECK_INTERVAL, STATUS_CHANGE_DELAY } from '../../../constants';
import type { CaptureStatus, DocSrcDimensions, Resolution, VideoRef } from '../types';
import computeSrcDimensions, { docDrawer } from '../utils/auto-capture';
const {
  logInfo,
  logWarn
} = getLogger({
  name: 'idv2',
  location: 'use-auto-capture-doc'
});
const FACE_CLEAR_REQUIRED = 4; // ~800ms at 200ms/frame
// Skip scanic after this long of quality-OK + scanic-fail (e.g. DL back on textured surface).
const SCANIC_BYPASS_MS = 3500;
const BYPASS_CANCEL_DIFF = 30; // cancel post-detection if frame changes by this mean diff
const BYPASS_STABLE_DIFF = 40; // restart bypass timer if scene changes during accumulation
const DETECTION_COOLDOWN_MS = 2000;
const sampleFrame = (canvas: HTMLCanvasElement): Uint8ClampedArray | null => {
  try {
    const thumb = document.createElement('canvas');
    thumb.width = 32;
    thumb.height = 20;
    const ctx = thumb.getContext('2d', {
      willReadFrequently: true
    });
    if (!ctx) return null;
    ctx.drawImage(canvas, 0, 0, 32, 20);
    return ctx.getImageData(0, 0, 32, 20).data;
  } catch {
    return null;
  }
};
const framesDiffer = (a: Uint8ClampedArray, b: Uint8ClampedArray, threshold = BYPASS_CANCEL_DIFF): boolean => {
  let sum = 0;
  for (let i = 0; i < a.length; i += 4) {
    sum += (Math.abs(a[i] - b[i]) + Math.abs(a[i + 1] - b[i + 1]) + Math.abs(a[i + 2] - b[i + 2])) / 3;
  }
  return sum / (a.length / 4) > threshold;
};
// Scale face threshold by canvas area so desktop (larger canvas, user farther back) still works.
const FACE_AREA_REF_CANVAS = 82_800; // mobile docDrawer ≈ 360×230
const MIN_REAL_FACE_AREA = (canvasArea: number) => 0.22 * Math.min(1, Math.sqrt(FACE_AREA_REF_CANVAS / canvasArea));
type AutoCaptureProps = {
  canvasRef: MutableRefObject<HTMLCanvasElement | undefined>;
  isCaptured: boolean;
  onDetectionComplete: () => void;
  onDetectionReset: () => void;
  onStatusChange: (currStatus: CaptureStatus | undefined) => void;
  outlineHeight: number;
  outlineOffsetX?: number;
  outlineOffsetY?: number;
  outlineWidth: number;
  videoRef: VideoRef;
  videoResolution?: Resolution;
  videoSize: Resolution | undefined;
};
const CountDownProps = {
  countStart: 3,
  // This is an arbitrary value - basically we want a few counts to countdown from and complete the countdown in STATUS_CHANGE_DELAY time
  intervalMs: STATUS_CHANGE_DELAY / 3
};
const isCardOk = (x: unknown) => x === CardCaptureStatus.OK;
const isNonZeroVideoSize = (v?: Resolution): v is Resolution => Boolean(v) && v?.width !== 0 && v?.height !== 0;
const useAutoCaptureDoc = ({
  canvasRef,
  isCaptured,
  onDetectionComplete,
  onDetectionReset,
  onStatusChange,
  outlineHeight,
  outlineOffsetX,
  outlineOffsetY,
  outlineWidth,
  videoRef,
  videoResolution,
  videoSize
}: AutoCaptureProps) => {
  const faceLandmarker = useFaceModel();
  const recentFaceDetectedRef = useRef<boolean[]>([]); // ring buffer: ALL recent must be face-free
  const wideCanvasRef = useRef<HTMLCanvasElement | undefined>(undefined); // reused wide canvas for scanic
  const successCountRef = useRef(0);
  const detectionCompletedRef = useRef(false);
  const pastStatusRef = useRef<CardCaptureStatus | undefined>(undefined);
  const lastResetTimestampRef = useRef<number>(0);
  const qualityOkScanicFailStartRef = useRef<number | null>(null); // start of quality-OK + scanic-fail streak
  const bypassDetectionDataRef = useRef<Uint8ClampedArray | null>(null); // frame thumbnail at bypass-triggered detection
  const bypassLastSampleRef = useRef<Uint8ClampedArray | null>(null); // previous frame for stability check during bypass
  // Everything below runs on a SELFIE_CHECK_INTERVAL timer, so any log here must be one-shot.
  const bypassLoggedRef = useRef(false);
  const scanicErrorLoggedRef = useRef(false);
  const faceVetoErrorLoggedRef = useRef(false);
  const [statusChangeDelayRunning, setStatusChangeDelayTimeRunning] = useState(false);
  const [detectionError, setDetectionError] = useState(false);
  const [waitVal, {
    startCountdown,
    resetCountdown
  }] = useCountdown(CountDownProps);

  /**
   * Width and height of the image that will be used for detection algos
   * We don't want these dimensions to be bigger than video size
   * We add a little bit of cushion space around the frame outline
   */
  const memoDocSrcDimensions: DocSrcDimensions | undefined = useMemo(() => isNonZeroVideoSize(videoSize) ? computeSrcDimensions(videoSize, {
    outlineWidth,
    outlineHeight,
    outlineOffsetY,
    videoRef,
    videoResolution,
    outlineOffsetX
  }) : undefined, [videoSize, outlineWidth, outlineHeight, outlineOffsetY, videoRef, videoResolution, outlineOffsetX]);
  const docImageDrawer = useCallback((vidRef: VideoRef) => !memoDocSrcDimensions || !canvasRef.current || !vidRef.current ? undefined : docDrawer(memoDocSrcDimensions, canvasRef, vidRef), [canvasRef, memoDocSrcDimensions]);
  const detectAndCaptureDocument = useCallback(async () => {
    if (isCaptured || !videoRef.current || !isNonZeroVideoSize(videoSize) || !docImageDrawer || detectionError) {
      return;
    }
    const refCanvas = docImageDrawer(videoRef);
    if (!refCanvas) {
      return;
    }
    let cardCaptureStatus = await getCardCaptureStatus(refCanvas, outlineWidth, outlineHeight);
    const qualityPassed = cardCaptureStatus === CardCaptureStatus.OK;

    // Scanic gate: verify a rectangular document boundary on a wider canvas (50% expansion each side).
    // Only runs when quality already passes. Bypassed after SCANIC_BYPASS_MS of sustained failure
    // (e.g. DL back on textured surface where scanic can't find corners).
    if (cardCaptureStatus === CardCaptureStatus.OK && videoRef.current && memoDocSrcDimensions && videoResolution?.width && videoResolution?.height) {
      try {
        const expandW = memoDocSrcDimensions.sWidth * 0.5;
        const expandH = memoDocSrcDimensions.sHeight * 0.5;
        const wideSx = Math.max(0, Math.round(memoDocSrcDimensions.sx - expandW / 2));
        const wideSy = Math.max(0, Math.round(memoDocSrcDimensions.sy - expandH / 2));
        const wideSWidth = Math.min(videoResolution.width, Math.round(memoDocSrcDimensions.sWidth + expandW));
        const wideSHeight = Math.min(videoResolution.height, Math.round(memoDocSrcDimensions.sHeight + expandH));
        if (!wideCanvasRef.current) {
          wideCanvasRef.current = document.createElement('canvas');
        }
        const wideCanvas = wideCanvasRef.current;
        const scale = Math.min(1, 800 / Math.max(wideSWidth, wideSHeight));
        wideCanvas.width = Math.round(wideSWidth * scale);
        wideCanvas.height = Math.round(wideSHeight * scale);
        const wideCtx = wideCanvas.getContext('2d');
        if (wideCtx) {
          wideCtx.drawImage(videoRef.current, wideSx, wideSy, wideSWidth, wideSHeight, 0, 0, wideCanvas.width, wideCanvas.height);
          const cornersFound = await hasDocumentCorners(wideCanvas);
          if (cornersFound) {
            qualityOkScanicFailStartRef.current = null;
            bypassLastSampleRef.current = null;
          } else {
            if (qualityOkScanicFailStartRef.current === null) {
              qualityOkScanicFailStartRef.current = Date.now();
              bypassLastSampleRef.current = sampleFrame(refCanvas);
            } else {
              // Subsequent fail — if scene changed (camera panning), restart timer.
              const currentSample = sampleFrame(refCanvas);
              if (currentSample && bypassLastSampleRef.current && framesDiffer(currentSample, bypassLastSampleRef.current, BYPASS_STABLE_DIFF)) {
                qualityOkScanicFailStartRef.current = Date.now();
              }
              bypassLastSampleRef.current = currentSample;
            }
          }
          const elapsed = qualityOkScanicFailStartRef.current !== null ? Date.now() - qualityOkScanicFailStartRef.current : 0;
          const bypassActive = elapsed >= SCANIC_BYPASS_MS;
          if (bypassActive && !cornersFound && !bypassLoggedRef.current) {
            bypassLoggedRef.current = true;
            logInfo('Scanic bypass activated', {
              elapsed,
              canvas: `${wideCanvas.width}×${wideCanvas.height}`
            });
          }
          if (!cornersFound && !bypassActive) {
            cardCaptureStatus = CardCaptureStatus.detecting;
          }
        }
      } catch (err) {
        if (!scanicErrorLoggedRef.current) {
          scanicErrorLoggedRef.current = true;
          logWarn('scanic gate error', err);
        }
      }
    }

    // Reset bypass timer when quality fails; keep it when quality passes but scanic fails.
    if (!qualityPassed) {
      qualityOkScanicFailStartRef.current = null;
      bypassLastSampleRef.current = null;
    }

    // Bypass-cancel: if the frame changed since bypass-triggered detection, the card moved.
    if (cardCaptureStatus === CardCaptureStatus.OK && detectionCompletedRef.current && bypassDetectionDataRef.current) {
      const currentData = sampleFrame(refCanvas);
      if (currentData && framesDiffer(currentData, bypassDetectionDataRef.current)) {
        logInfo('bypass-cancel: frame changed, cancelling');
        cardCaptureStatus = CardCaptureStatus.detecting;
      }
    }

    // Face veto: reject frames with a real face (ring buffer prevents per-frame misses).
    if (cardCaptureStatus === CardCaptureStatus.OK && faceLandmarker) {
      try {
        const faceResult = faceLandmarker.detect(refCanvas);
        let faceArea = 0;
        const landmarks = faceResult.faceLandmarks[0];
        if (landmarks && landmarks.length > 0) {
          let minX = 1,
            maxX = 0,
            minY = 1,
            maxY = 0;
          for (const pt of landmarks) {
            if (pt.x < minX) minX = pt.x;
            if (pt.x > maxX) maxX = pt.x;
            if (pt.y < minY) minY = pt.y;
            if (pt.y > maxY) maxY = pt.y;
          }
          faceArea = (maxX - minX) * (maxY - minY);
        }
        const faceDetected = faceArea >= MIN_REAL_FACE_AREA(refCanvas.width * refCanvas.height);
        recentFaceDetectedRef.current.push(faceDetected);
        if (recentFaceDetectedRef.current.length > FACE_CLEAR_REQUIRED) {
          recentFaceDetectedRef.current.shift();
        }
        const faceRecentlyDetected = recentFaceDetectedRef.current.some(v => v);
        if (faceRecentlyDetected) {
          cardCaptureStatus = CardCaptureStatus.detecting;
        }
      } catch (err_0) {
        if (!faceVetoErrorLoggedRef.current) {
          faceVetoErrorLoggedRef.current = true;
          logWarn('face veto error', err_0);
        }
      }
    }
    if (cardCaptureStatus === CardCaptureStatus.error && !detectionError) {
      setDetectionError(true);
    }
    if (isCardOk(cardCaptureStatus)) {
      successCountRef.current += 1;
      resetCountdown();
      setStatusChangeDelayTimeRunning(false);
      if (successCountRef.current >= REQUIRED_SUCCESSES) {
        onStatusChange(CardCaptureStatus.OK);
        if (!detectionCompletedRef.current && Date.now() - lastResetTimestampRef.current >= DETECTION_COOLDOWN_MS) {
          detectionCompletedRef.current = true;
          const bypassElapsed = qualityOkScanicFailStartRef.current !== null ? Date.now() - qualityOkScanicFailStartRef.current : 0;
          if (bypassElapsed >= SCANIC_BYPASS_MS) {
            bypassDetectionDataRef.current = sampleFrame(refCanvas);
          }
          onDetectionComplete();
        }
      }
    } else {
      successCountRef.current = 0;
      if (detectionCompletedRef.current) {
        detectionCompletedRef.current = false;
        qualityOkScanicFailStartRef.current = null;
        bypassDetectionDataRef.current = null;
        bypassLastSampleRef.current = null;
        lastResetTimestampRef.current = Date.now();
        onStatusChange(cardCaptureStatus);
        onDetectionReset();
      } else {
        if (!statusChangeDelayRunning) {
          startCountdown();
          setStatusChangeDelayTimeRunning(true);
        }
      }
    }
    pastStatusRef.current = cardCaptureStatus;
  }, [docImageDrawer, isCaptured, onDetectionComplete, onDetectionReset, onStatusChange, outlineHeight, outlineWidth, resetCountdown, startCountdown, statusChangeDelayRunning, videoRef, videoSize, detectionError, faceLandmarker, memoDocSrcDimensions, videoResolution]);

  // Timer-based fallback for the pre-detection case: if the card leaves before detection
  // completes, wait STATUS_CHANGE_DELAY before changing status to avoid flickering on
  // transient misses.
  useEffect(() => {
    if (waitVal === 0) {
      if (!isCardOk(pastStatusRef.current)) {
        onStatusChange(pastStatusRef.current);
        successCountRef.current = 0;
        detectionCompletedRef.current = false;
        bypassDetectionDataRef.current = null;
        bypassLastSampleRef.current = null;
        // NOTE: do NOT reset qualityOkScanicFailStartRef here — this countdown fires every
        // STATUS_CHANGE_DELAY (400ms) and would wipe the bypass timer before it can accumulate.
        lastResetTimestampRef.current = Date.now();
        resetCountdown();
        setStatusChangeDelayTimeRunning(false);
        onDetectionReset();
      }
    }
  }, [waitVal, onStatusChange, resetCountdown, onDetectionReset]);
  useEffect(() => {
    const intervalId = setInterval(async () => {
      if (isCaptured || detectionError) {
        clearInterval(intervalId);
        return;
      }
      await detectAndCaptureDocument();
    }, SELFIE_CHECK_INTERVAL);
    return () => clearInterval(intervalId);
  }, [detectAndCaptureDocument, isCaptured, detectionError]);
};
export default useAutoCaptureDoc;